"use client";

import React, { useRef } from "react";
import Image from "next/image";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import { Motion } from "@/components/global/utils/Motion";
import bg from "@/public/global/bg-art.jpg";
import heroImg from "@/public/aboutUs/heroImg.png";
import { PageEigenePriceSchema } from "@/sanity/pages/PageEigenePriceTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { useScroll, useTransform } from "framer-motion";


export default function Hero({ data }: { data: PageEigenePriceSchema }) {

  return (
    <section className="w-full pt-[2.25rem] lg:pt-[5rem] px-[2rem] lg:px-[5rem]">
      <div className="relative mx-auto w-full bg-gradient-to-b max-w-[230.25rem] pt-[10rem] lg:pt-[16rem] rounded-[2.25rem] lg:rounded-[5.25rem] overflow-clip">
        <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB] z-0">
          <div className="absolute left-0 top-0 w-full lg:w-[65%] h-full bg-gradient-to-b lg:bg-gradient-to-r from-[#E6F2EB]/90 via-[#E6F2EB]/[35%] to-transparent z-[1]"></div>
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-[50%_20%] mix-blend-luminosity lg:opacity-[.36] opacity-[.1] z-[0]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>
        
        <div className="relative flex flex-col items-center mx-auto lg:max-w-[82.625rem] pt-[6.91rem] pb-[10rem] lg:pt-[8rem] lg:pb-[21.37rem] z-[1] text-center">
          {data.heroChip && <ChipSerif text={data.heroChip} />}

          {data?.heroTitle && (
            <PortableText
              value={data?.heroTitle ?? {}}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <div className="w-full overflow-clip">
                      <Motion
                        type="h2"
                        className="font-instrument-sans text-[#103037] text-[5rem] lg:text-[7rem] leading-[112%] font-[600] tracking-[-0.165rem] lg:tracking-[-0.21rem]"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.3,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                      >
                        {children}
                      </Motion>
                    </div>
                  ),
                  normal: ({ children }) => (
                    <div className="max-w-[41rem] lg:max-w-[46.75rem] w-full overflow-clip">
                      <Motion
                        type="p"
                        className="text-black/70 text-[2rem] font-[400] leading-[150%] mb-[4.5rem] lg:mb-[3.25rem]"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                      >
                        {children}
                      </Motion>
                    </div>
                  ),
                },
              })}
            />
          )}

          <div className="flex flex-col lg:flex-row items-center gap-[2rem] w-[75vw] lg:w-auto">
            {data.heroCta1 && <CTAButton data={data.heroCta1} />}
          </div>
        </div>
      </div>
    </section>
  );
}
